/**
 * Профайлер для REST запитів
 * Відображає інформацію про всі HTTP запити, виконані через RestClientService
 */

import {
    ApiOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    SearchOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { Badge, Button, Descriptions, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import React, { useMemo, useState } from 'react'
import { requestProfiler, type RequestProfile } from '../../../Services/RestClientService/RequestProfiler'
import type { DebugProfilerData, DebugProfilerInterface } from '../types'

const { Text } = Typography
const { Option } = Select

/**
 * Компонент для відображення деталей запиту
 */
const RequestDetailsModal: React.FC<{
    request: RequestProfile
}> = ({ request }) => {
    const [isVisible, setIsVisible] = useState(false)

    const getStatusColor = (statusCode?: number): string => {
        if (!statusCode) return 'default'
        if (statusCode >= 200 && statusCode < 300) return 'success'
        if (statusCode >= 300 && statusCode < 400) return 'processing'
        if (statusCode >= 400 && statusCode < 500) return 'warning'
        return 'error'
    }

    const formatJson = (data: any): string => {
        try {
            return JSON.stringify(data, null, 2)
        } catch {
            return String(data)
        }
    }

    const formatHeaders = (headers?: Record<string, string>): React.ReactNode => {
        if (!headers || Object.keys(headers).length === 0) {
            return <Text type="secondary">Немає заголовків</Text>
        }

        return (
            <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                {Object.entries(headers).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: '11px' }}>
                            {key}:
                        </Text>{' '}
                        <Text code style={{ fontSize: '11px' }}>
                            {value}
                        </Text>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <>
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setIsVisible(true)}
                style={{ padding: 0, height: 'auto' }}
            >
                Деталі
            </Button>
            <Modal
                title={
                    <Space>
                        <Tag color={getStatusColor(request.statusCode)}>{request.method}</Tag>
                        <Text style={{ fontSize: '14px' }}>{request.url}</Text>
                    </Space>
                }
                open={isVisible}
                onCancel={() => setIsVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setIsVisible(false)}>
                        Закрити
                    </Button>,
                ]}
                width={1000}
            >
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Метод" span={1}>
                        <Tag color="blue">{request.method}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Статус" span={1}>
                        {request.statusCode ? (
                            <Badge
                                status={getStatusColor(request.statusCode) as any}
                                text={`${request.statusCode} ${request.statusText || ''}`}
                            />
                        ) : (
                            <Text type="secondary">В процесі</Text>
                        )}
                    </Descriptions.Item>

                    <Descriptions.Item label="URL" span={2}>
                        <Text code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                            {request.url}
                        </Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Pathname" span={2}>
                        <Text code>{request.pathname}</Text>
                    </Descriptions.Item>

                    {request.tags && request.tags.length > 0 && (
                        <Descriptions.Item label="Теги" span={2}>
                            {request.tags.map((tag) => (
                                <Tag key={tag} color="blue">
                                    {tag}
                                </Tag>
                            ))}
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Тривалість" span={1}>
                        <Text strong>{request.duration ? `${request.duration} мс` : 'В процесі...'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Розмір відповіді" span={1}>
                        {request.responseSize ? (
                            <Text>{(request.responseSize / 1024).toFixed(2)} KB</Text>
                        ) : (
                            <Text type="secondary">-</Text>
                        )}
                    </Descriptions.Item>

                    {request.requestSize && (
                        <Descriptions.Item label="Розмір запиту" span={2}>
                            <Text>{(request.requestSize / 1024).toFixed(2)} KB</Text>
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Час початку" span={1}>
                        <Text>{new Date(request.startTime).toLocaleTimeString()}</Text>
                    </Descriptions.Item>
                    {request.endTime && (
                        <Descriptions.Item label="Час завершення" span={1}>
                            <Text>{new Date(request.endTime).toLocaleTimeString()}</Text>
                        </Descriptions.Item>
                    )}

                    {(request.fromCache || request.wasRetried) && (
                        <Descriptions.Item label="Додаткова інформація" span={2}>
                            {request.fromCache && <Tag color="cyan">З кешу</Tag>}
                            {request.wasRetried && <Tag color="orange">Повторні спроби: {request.retryCount}</Tag>}
                        </Descriptions.Item>
                    )}

                    {request.searchParams && Object.keys(request.searchParams).length > 0 && (
                        <Descriptions.Item label="Query параметри" span={2}>
                            <pre
                                style={{
                                    maxHeight: '150px',
                                    overflow: 'auto',
                                    fontSize: '11px',
                                    background: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                }}
                            >
                                {formatJson(request.searchParams)}
                            </pre>
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Заголовки запиту" span={2}>
                        {formatHeaders(request.headers)}
                    </Descriptions.Item>

                    {request.body && (
                        <Descriptions.Item label="Тіло запиту" span={2}>
                            <pre
                                style={{
                                    maxHeight: '200px',
                                    overflow: 'auto',
                                    fontSize: '11px',
                                    background: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                }}
                            >
                                {formatJson(request.body)}
                            </pre>
                        </Descriptions.Item>
                    )}

                    {request.responseHeaders && (
                        <Descriptions.Item label="Заголовки відповіді" span={2}>
                            {formatHeaders(request.responseHeaders)}
                        </Descriptions.Item>
                    )}

                    {request.responseBody && (
                        <Descriptions.Item label="Тіло відповіді" span={2}>
                            <pre
                                style={{
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    fontSize: '11px',
                                    background: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: '4px',
                                }}
                            >
                                {formatJson(request.responseBody)}
                            </pre>
                        </Descriptions.Item>
                    )}

                    {request.hasError && (
                        <>
                            <Descriptions.Item label="Помилка" span={2}>
                                <Text type="danger">{request.errorMessage}</Text>
                            </Descriptions.Item>
                            {request.errorStack && (
                                <Descriptions.Item label="Стек помилки" span={2}>
                                    <pre
                                        style={{
                                            maxHeight: '200px',
                                            overflow: 'auto',
                                            fontSize: '10px',
                                            background: '#fff1f0',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        {request.errorStack}
                                    </pre>
                                </Descriptions.Item>
                            )}
                        </>
                    )}
                </Descriptions>
            </Modal>
        </>
    )
}

/**
 * Компонент для відображення таблиці запитів
 */
const RequestsTable: React.FC<{ requests: RequestProfile[] }> = ({ requests }) => {
    const [searchText, setSearchText] = useState('')
    const [methodFilter, setMethodFilter] = useState<string | undefined>(undefined)
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
    const [tagFilter, setTagFilter] = useState<string | undefined>(undefined)
    const [sortField, setSortField] = useState<'duration' | 'size' | 'time'>('time')
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

    // Отримуємо унікальні методи
    const methods = useMemo(() => {
        const uniqueMethods = [...new Set(requests.map((r) => r.method))]
        return uniqueMethods.sort()
    }, [requests])

    // Отримуємо унікальні теги
    const tags = useMemo(() => {
        const allTags = new Set<string>()
        requests.forEach((r) => {
            r.tags?.forEach((tag) => allTags.add(tag))
        })
        return Array.from(allTags).sort()
    }, [requests])

    // Фільтрація даних
    const filteredRequests = useMemo(() => {
        let filtered = requests.filter((request) => {
            // Пошук по підстроці
            const searchMatch =
                searchText === '' ||
                request.url.toLowerCase().includes(searchText.toLowerCase()) ||
                request.method.toLowerCase().includes(searchText.toLowerCase()) ||
                request.pathname.toLowerCase().includes(searchText.toLowerCase())

            // Фільтр за методом
            const methodMatch = methodFilter === undefined || request.method === methodFilter

            // Фільтр за статусом
            let statusMatch = true
            if (statusFilter) {
                if (statusFilter === 'success') {
                    statusMatch =
                        !request.hasError &&
                        request.statusCode !== undefined &&
                        request.statusCode >= 200 &&
                        request.statusCode < 300
                } else if (statusFilter === 'error') {
                    statusMatch =
                        request.hasError ||
                        (request.statusCode !== undefined && (request.statusCode >= 400 || request.statusCode < 200))
                } else if (statusFilter === 'pending') {
                    statusMatch = request.endTime === undefined
                }
            }

            // Фільтр за тегом
            const tagMatch = tagFilter === undefined || request.tags?.includes(tagFilter)

            return searchMatch && methodMatch && statusMatch && tagMatch
        })

        // Сортування
        filtered.sort((a, b) => {
            let comparison = 0

            switch (sortField) {
                case 'duration':
                    comparison = (a.duration || 0) - (b.duration || 0)
                    break
                case 'size':
                    comparison = (a.responseSize || 0) - (b.responseSize || 0)
                    break
                case 'time':
                    comparison = a.startTime - b.startTime
                    break
            }

            return sortOrder === 'asc' ? comparison : -comparison
        })

        return filtered
    }, [requests, searchText, methodFilter, statusFilter, tagFilter, sortField, sortOrder])

    const getMethodColor = (method: string): string => {
        const colors: Record<string, string> = {
            GET: 'blue',
            POST: 'green',
            PUT: 'orange',
            PATCH: 'purple',
            DELETE: 'red',
        }
        return colors[method] || 'default'
    }

    const getStatusColor = (request: RequestProfile): string => {
        if (request.hasError) return 'error'
        if (!request.statusCode) return 'processing'
        if (request.statusCode >= 200 && request.statusCode < 300) return 'success'
        if (request.statusCode >= 400) return 'error'
        return 'warning'
    }

    const formatDuration = (duration?: number): React.ReactNode => {
        if (duration === undefined) {
            return <Text type="secondary">В процесі...</Text>
        }

        let color = 'inherit'
        if (duration > 2000)
            color = '#ff4d4f' // Червоний для дуже повільних
        else if (duration > 1000)
            color = '#faad14' // Жовтий для повільних
        else if (duration > 500) color = '#1890ff' // Синій для середніх

        return <Text style={{ color, fontWeight: 500 }}>{duration} мс</Text>
    }

    const columns = [
        {
            title: 'Метод',
            dataIndex: 'method',
            key: 'method',
            width: '8%',
            render: (method: string) => <Tag color={getMethodColor(method)}>{method}</Tag>,
        },
        {
            title: 'URL',
            dataIndex: 'pathname',
            key: 'pathname',
            width: '30%',
            render: (_: string, record: RequestProfile) => (
                <Tooltip title={record.url}>
                    <div>
                        <Text
                            code
                            style={{
                                fontSize: '11px',
                                maxWidth: '100%',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {record.pathname}
                        </Text>
                        {record.tags && record.tags.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                                {record.tags.slice(0, 3).map((tag) => (
                                    <Tag key={tag} style={{ fontSize: '10px', padding: '0 4px' }}>
                                        {tag}
                                    </Tag>
                                ))}
                                {record.tags.length > 3 && (
                                    <Tooltip title={record.tags.slice(3).join(', ')}>
                                        <Tag style={{ fontSize: '10px', padding: '0 4px' }}>
                                            +{record.tags.length - 3}
                                        </Tag>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Статус',
            key: 'status',
            width: '12%',
            render: (_: any, record: RequestProfile) => {
                if (record.hasError) {
                    return (
                        <Tooltip title={record.errorMessage}>
                            <Tag icon={<CloseCircleOutlined />} color="error">
                                Помилка
                            </Tag>
                        </Tooltip>
                    )
                }

                if (!record.statusCode) {
                    return (
                        <Tag icon={<ClockCircleOutlined />} color="processing">
                            В процесі
                        </Tag>
                    )
                }

                const color = getStatusColor(record)
                const icon =
                    color === 'success' ? (
                        <CheckCircleOutlined />
                    ) : color === 'error' ? (
                        <CloseCircleOutlined />
                    ) : (
                        <WarningOutlined />
                    )

                return (
                    <Space orientation="vertical" size={0}>
                        <Tag icon={icon} color={color}>
                            {record.statusCode}
                        </Tag>
                        {record.fromCache && (
                            <Tag color="cyan" style={{ fontSize: '10px' }}>
                                Кеш
                            </Tag>
                        )}
                        {record.wasRetried && (
                            <Tag color="orange" style={{ fontSize: '10px' }}>
                                Retry x{record.retryCount}
                            </Tag>
                        )}
                    </Space>
                )
            },
        },
        {
            title: 'Тривалість',
            dataIndex: 'duration',
            key: 'duration',
            width: '12%',
            sorter: (a: RequestProfile, b: RequestProfile) => (a.duration || 0) - (b.duration || 0),
            render: (duration: number | undefined) => formatDuration(duration),
        },
        {
            title: 'Розмір',
            key: 'size',
            width: '10%',
            render: (_: any, record: RequestProfile) => {
                const size = record.responseSize
                if (!size) return <Text type="secondary">-</Text>
                if (size < 1024) return <Text>{size} B</Text>
                if (size < 1024 * 1024) return <Text>{(size / 1024).toFixed(1)} KB</Text>
                return <Text>{(size / (1024 * 1024)).toFixed(2)} MB</Text>
            },
        },
        {
            title: 'Час',
            dataIndex: 'startTime',
            key: 'startTime',
            width: '12%',
            render: (time: number) => <Text style={{ fontSize: '11px' }}>{new Date(time).toLocaleTimeString()}</Text>,
        },
        {
            title: '',
            key: 'actions',
            width: '8%',
            render: (_: any, record: RequestProfile) => <RequestDetailsModal request={record} />,
        },
    ]

    return (
        <div>
            <Space style={{ marginBottom: 16, width: '100%' }} wrap>
                <Input
                    placeholder="Пошук по URL або методу..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                    allowClear
                />
                <Select
                    placeholder="Метод"
                    value={methodFilter}
                    onChange={setMethodFilter}
                    style={{ width: 120 }}
                    allowClear
                >
                    {methods.map((method) => (
                        <Option key={method} value={method}>
                            {method}
                        </Option>
                    ))}
                </Select>
                <Select
                    placeholder="Статус"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 150 }}
                    allowClear
                >
                    <Option value="success">Успішні</Option>
                    <Option value="error">З помилками</Option>
                    <Option value="pending">В процесі</Option>
                </Select>
                {tags.length > 0 && (
                    <Select
                        placeholder="Тег"
                        value={tagFilter}
                        onChange={setTagFilter}
                        style={{ width: 150 }}
                        allowClear
                    >
                        {tags.map((tag) => (
                            <Option key={tag} value={tag}>
                                {tag}
                            </Option>
                        ))}
                    </Select>
                )}
                <Select value={sortField} onChange={setSortField} style={{ width: 150 }}>
                    <Option value="time">За часом</Option>
                    <Option value="duration">За тривалістю</Option>
                    <Option value="size">За розміром</Option>
                </Select>
                <Button size="small" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                    {sortOrder === 'asc' ? '↑' : '↓'}
                </Button>
                <Text type="secondary">
                    Знайдено: {filteredRequests.length} з {requests.length}
                </Text>
                <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                        requestProfiler.clear()
                    }}
                >
                    Очистити
                </Button>
                <Button
                    size="small"
                    onClick={() => {
                        const json = requestProfiler.exportStatsToJson()
                        const blob = new Blob([json], { type: 'application/json' })
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `rest-requests-${Date.now()}.json`
                        a.click()
                    }}
                >
                    Експорт
                </Button>
            </Space>

            <Table
                columns={columns}
                dataSource={filteredRequests}
                rowKey="id"
                size="small"
                pagination={{
                    pageSize: 50,
                    showSizeChanger: true,
                    showTotal: (total) => `Всього: ${total} запитів`,
                }}
                scroll={{ x: 1200 }}
            />
        </div>
    )
}

/**
 * Клас профайлера для REST запитів
 */
export class RestRequestProfiler implements DebugProfilerInterface {
    key = 'rest-requests'
    name = 'REST Запити'
    icon = (<ApiOutlined />)
    priority = 95

    isEnabled(): boolean {
        const env = (import.meta as any).env
        return env.DEV && requestProfiler.enabled
    }

    async getData(): Promise<DebugProfilerData> {
        try {
            const stats = requestProfiler.getStats()
            const requests = requestProfiler.getAllRequests()

            // Визначаємо загальний статус
            let status: 'success' | 'warning' | 'error' = 'success'
            if (stats.serverErrors > 0 || stats.clientErrors > 5) {
                status = 'error'
            } else if (stats.clientErrors > 0 || stats.avgDuration > 1000) {
                status = 'warning'
            }

            return {
                summary: {
                    count: stats.total,
                    status,
                    label: `${stats.successful}/${stats.total} успішних, ${stats.avgDuration}мс середня`,
                },
                details: {
                    stats: [
                        {
                            title: 'Всього запитів',
                            value: stats.total,
                            color: '#1890ff',
                            description: 'Загальна кількість виконаних запитів',
                        },
                        {
                            title: 'Успішні (2xx)',
                            value: stats.successful,
                            color: '#52c41a',
                            description: 'Запити зі статусом 200-299',
                        },
                        {
                            title: 'Помилки клієнта (4xx)',
                            value: stats.clientErrors,
                            color: '#faad14',
                            description: 'Запити зі статусом 400-499',
                        },
                        {
                            title: 'Помилки сервера (5xx)',
                            value: stats.serverErrors,
                            color: '#ff4d4f',
                            description: 'Запити зі статусом 500+',
                        },
                        {
                            title: 'В процесі',
                            value: stats.pending,
                            color: '#1890ff',
                            description: 'Запити, що виконуються зараз',
                        },
                        {
                            title: 'Середня тривалість',
                            value: `${stats.avgDuration} мс`,
                            color: stats.avgDuration > 1000 ? '#faad14' : '#52c41a',
                            description: 'Середній час виконання запиту',
                        },
                        {
                            title: 'Найповільніший',
                            value: `${stats.slowestDuration} мс`,
                            color: stats.slowestDuration > 2000 ? '#ff4d4f' : '#faad14',
                            description: 'Найдовший запит',
                        },
                        {
                            title: 'Найшвидший',
                            value: `${stats.fastestDuration} мс`,
                            color: '#52c41a',
                            description: 'Найкоротший запит',
                        },
                        {
                            title: 'Загальний обсяг',
                            value: `${(stats.totalDataSize / 1024 / 1024).toFixed(2)} MB`,
                            color: '#722ed1',
                            description: 'Загальний розмір отриманих даних',
                        },
                    ],
                    custom: <RequestsTable requests={requests} />,
                },
            }
        } catch (error) {
            console.error('Помилка отримання даних REST запитів:', error)

            return {
                summary: {
                    count: 0,
                    status: 'error',
                    label: 'Помилка',
                },
                details: {
                    stats: [
                        {
                            title: 'Помилка',
                            value: error instanceof Error ? error.message : 'Невідома помилка',
                            color: '#ff4d4f',
                        },
                    ],
                },
            }
        }
    }
}
